import React, { useState } from 'react';
import { Divider, Text, Toggle } from '@spark';
import Hyperparams from './Hyperparams.jsx';
import HardwareDeviceSelector from './HardwareDeviceSelector.jsx';
import RuntimeStrategySelector from './RuntimeStrategySelector.jsx';
import { isGenerativeTask } from '@/shared/domain/taskTypes';
import { isNerTaskType } from '@/features/trainings/constants';
import { useCanManagePlatformSettings } from '@/features/auth/providerAccess';
import { useDemoTourActive, useWalkthroughDrivenState } from '@/features/walkthrough';

/** Walkthrough step that opens this accordion (`data-walkthrough` anchor). */
export const ADVANCED_SETTINGS_WALKTHROUGH_STEP = 'advanced-settings';

/**
 * Advanced training options accordion (Figma 3001-68512 / 3121-6533).
 * Toggle in the header expands Training Device, Metric Optimisation, and
 * generative-specific controls — matching the Train / Model Summary layout.
 * Prediction Strategy is classification-only (hidden for NER). Base model
 * selection is shown for every task type. "Optimize on"
 * (`rareClassLabelOptions`) appears only for two-label single-label
 * classification tasks.
 *
 * Training Device follows the platform-settings gate
 * (`useCanManagePlatformSettings`): on SaaS the hardware is SpecificAI's, so
 * only a god may pick CPU/GPU here (the same rule that hides Settings >
 * Hardware); everyone else keeps the platform default already in the form.
 *
 * The guided walkthrough opens the accordion while its "Adjust advanced
 * settings" step is up and closes it again when the step is left, unless
 * the host reports the user changed something in it (`advancedTouched`).
 * The read-only "Show me how" demo has no mark on Train at all: there the
 * accordion simply starts open (Figma 9455-7106, last frame).
 *
 * Whether the student prompts are included in training & evaluation is now
 * configured on Model Configuration > SpecificAI model ("Disable" per field),
 * not here.
 */
const Advanced = (props) => {
    const demoTour = useDemoTourActive();
    const [open, setOpen] = useState(demoTour);
    const isGenerative = isGenerativeTask(props.taskType);
    const canPickTrainingDevice = useCanManagePlatformSettings();
    useWalkthroughDrivenState(ADVANCED_SETTINGS_WALKTHROUGH_STEP, open, setOpen, {
        keepIf: props.advancedTouched === true,
    });

    return (
        <div className="train-advanced fs-unmask" data-walkthrough={ADVANCED_SETTINGS_WALKTHROUGH_STEP}>
            <div className="train-advanced__header">
                <Text variant="lead-semibold" as="div">
                    Advanced
                </Text>
                <Toggle
                    active={open}
                    onChange={(e) => setOpen(e.target.checked)}
                    aria-label="Advanced training options"
                />
            </div>

            {open && (
                <div className="train-advanced__body">
                    {!isGenerative && canPickTrainingDevice && (
                        <HardwareDeviceSelector
                            trainingDevice={props.trainingDevice}
                            onHardwareDeviceChange={props.onHardwareDeviceChange}
                        />
                    )}
                    {!isGenerative && (
                        <Hyperparams
                            metricOptimization={props.metricOptimization}
                            onMetricOptimizationChange={props.onMetricOptimizationChange}
                            betaForFScore={props.betaForFScore}
                            onFScoreBetaChange={props.onFScoreBetaChange}
                            rareClassLabel={props.rareClassLabel}
                            onRareClassLabelChange={props.onRareClassLabelChange}
                            rareClassLabelOptions={props.rareClassLabelOptions}
                        />
                    )}
                    {isGenerative && props.accuracyMetricsSelector && (
                        <>
                            {props.accuracyMetricsSelector}
                            <Divider />
                        </>
                    )}
                    {isGenerative && props.judgeCriteriaSelector}
                    {isGenerative && props.classicMetricsSelector && (
                        <>
                            <Divider />
                            {props.classicMetricsSelector}
                        </>
                    )}
                    {!isGenerative && !isNerTaskType(props.taskType) && (
                        <RuntimeStrategySelector
                            predictionStrategy={props.predictionStrategy}
                            onPredictionStrategyChange={props.onPredictionStrategyChange}
                        />
                    )}
                    {props.baseModelSelector && (
                        <>
                            <Divider />
                            {props.baseModelSelector}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default Advanced;
